import { useSyncExternalStore } from 'react';

export type AnalyticsConsent = 'granted' | 'denied';

const CONSENT_KEY = 'c214:analytics-consent';
// Відмову не запамʼятовуємо назавжди: через 90 днів плашка зʼявляється знову. Прийняття
// не спливає. Значення в сховищі: 'granted' або 'denied:<мс від епохи>'.
const DENIED_TTL_MS = 90 * 24 * 60 * 60 * 1000;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// Вибір живе в localStorage, але приватний режим може кидати на будь-якому
// доступі — тоді вибір тримається в памʼяті до перезавантаження сторінки.
function readStored(): AnalyticsConsent | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (raw === 'granted') return 'granted';
    if (raw === null || !raw.startsWith('denied')) return null;
    const deniedAt = Number(raw.slice('denied:'.length));
    if (!Number.isFinite(deniedAt) || raw === 'denied') {
      // Значення без дати (записане до появи строку): відлік ведемо від першого читання.
      window.localStorage.setItem(CONSENT_KEY, storedValue('denied'));
      return 'denied';
    }
    if (Date.now() - deniedAt >= DENIED_TTL_MS) {
      window.localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return 'denied';
  } catch {
    return null;
  }
}

function storedValue(value: AnalyticsConsent): string {
  return value === 'denied' ? `denied:${Date.now()}` : value;
}

let current: AnalyticsConsent | null | undefined;
const listeners = new Set<() => void>();

function getConsent(): AnalyticsConsent | null {
  if (current === undefined) current = readStored();
  return current;
}

function setConsent(value: AnalyticsConsent | null): void {
  current = value;
  try {
    if (value) window.localStorage.setItem(CONSENT_KEY, storedValue(value));
    else window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    // лишається лише в памʼяті
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAnalyticsConsent(): AnalyticsConsent | null {
  return useSyncExternalStore(subscribe, getConsent, () => null);
}

export function writeConsent(value: AnalyticsConsent): void {
  setConsent(value);
}

// «Змінити налаштування cookie» у політиці: банер зʼявляється знову, а вже
// завантажений на цій сторінці GA вимикається через useGoogleAnalytics.
export function clearConsent(): void {
  setConsent(null);
}

function disableFlag(measurementId: string): string {
  return `ga-disable-${measurementId}`;
}

let loadedId: string | null = null;

export function loadGoogleAnalytics(measurementId: string): void {
  (window as unknown as Record<string, boolean>)[disableFlag(measurementId)] = false;
  if (loadedId === measurementId) return;
  loadedId = measurementId;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // biome-ignore lint/complexity/noArguments: gtag.js розбирає саме обʼєкт arguments
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  // Перегляди SPA-маршрутів рахує вбудоване в GA4 «зміни сторінки за подіями історії».
  window.gtag('config', measurementId);
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

export function disableGoogleAnalytics(measurementId: string): void {
  (window as unknown as Record<string, boolean>)[disableFlag(measurementId)] = true;
}
